import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import usePaginatedResource from '../../../hooks/usePaginatedResource';
import useResourceMutation from '../../../hooks/useResourceMutation';
import PageHeader from '../../../components/shared/PageHeader/PageHeader';
import DataTable, { Column } from '../../../components/shared/DataTable/DataTable';
import Modal from '../../../components/shared/Modal/Modal';
import ConfirmDialog from '../../../components/shared/ConfirmDialog/ConfirmDialog';
import Button from '../../../components/shared/Button/Button';
import FormField from '../../../components/shared/FormField/FormField';
import Input from '../../../components/shared/Input/Input';
import { createStop, updateStop, deleteStop } from '../../../services/stops';
import type { Stop } from '../../../types/api';

interface FormState { name: string; is_active: boolean; }
const empty: FormState = { name: '', is_active: true };

const StopsPage: React.FC = () => {
  const { data: stops, loading, refetch } = usePaginatedResource<Stop>('/stops/?include_inactive=true');
  const [editing, setEditing] = useState<Stop | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(empty);
  const [deleteTarget, setDeleteTarget] = useState<Stop | null>(null);

  const save = useResourceMutation(
    async (state: FormState) => editing
      ? updateStop(editing.id, state)
      : createStop(state),
    { successMessage: editing ? 'Stop updated' : 'Stop created' },
  );
  const del = useResourceMutation((id: number) => deleteStop(id), { successMessage: 'Stop removed' });

  const openCreate = () => { setEditing(null); setForm(empty); setShowForm(true); };
  const openEdit = (s: Stop) => {
    setEditing(s);
    setForm({ name: s.name, is_active: s.is_active });
    setShowForm(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await save.run(form);
      setShowForm(false); setForm(empty); setEditing(null);
      refetch();
    } catch { /* toast */ }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try { await del.run(deleteTarget.id); setDeleteTarget(null); refetch(); }
    catch { /* toast */ }
  };

  const columns: Column<Stop>[] = [
    { key: 'name', header: 'Stop', sortable: true },
    { key: 'is_active', header: 'Active', render: (s) => (
      <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
        s.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'
      }`}>
        <span className={`h-1.5 w-1.5 rounded-full ${s.is_active ? 'bg-emerald-500' : 'bg-slate-400'}`} aria-hidden />
        {s.is_active ? 'Active' : 'Inactive'}
      </span>
    ) },
  ];
  const actions = (s: Stop) => (
    <div className="inline-flex items-center gap-2">
      <Button variant="ghost" size="sm" onClick={() => openEdit(s)}>Edit</Button>
      <Button variant="danger" size="sm" onClick={() => setDeleteTarget(s)}>Delete</Button>
    </div>
  );

  return (
    <>
      <PageHeader
        title="Stops"
        description="Named points used to build routes."
        actions={<Button onClick={openCreate} icon={<Plus className="h-4 w-4" />}>New stop</Button>}
      />
      <DataTable<Stop>
        columns={columns}
        data={stops}
        rowKey="id"
        loading={loading}
        emptyTitle="No stops yet"
        emptyDescription="Add named points before defining routes."
        actions={actions}
      />
      <Modal isOpen={showForm} onClose={() => setShowForm(false)}
        title={editing ? 'Edit stop' : 'New stop'} size="sm"
        footer={<>
          <Button variant="secondary" onClick={() => setShowForm(false)} disabled={save.loading}>Cancel</Button>
          <Button onClick={handleSave} loading={save.loading}>{editing ? 'Save changes' : 'Create stop'}</Button>
        </>}>
        <form onSubmit={handleSave} className="grid gap-4">
          <FormField label="Name" name="name" required error={save.fieldErrors.name}>
            <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </FormField>
          <FormField label="Active" name="is_active">
            <label className="inline-flex items-center gap-2.5 rounded-lg border border-slate-300 bg-white px-3.5 h-11">
              <input type="checkbox" checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600" />
              <span className="text-sm text-slate-700">Stop is active</span>
            </label>
          </FormField>
        </form>
      </Modal>
      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Remove stop?"
        message={`Remove "${deleteTarget?.name ?? ''}"? Will fail if the stop is on any active route.`}
        confirmLabel="Remove"
        variant="danger"
        loading={del.loading}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
};

export default StopsPage;
